<template>
  <Page :title="pageTitle">
    <template #actions>
      <div class="flex items-center gap-4 text-sm">
        <span v-if="serverError" class="text-red-600" data-cy="cf-error">{{
          serverError
        }}</span>
        <Transition name="cf-fade" mode="out-in">
          <span
            :key="saveStatus"
            class="flex items-center gap-1.5 text-gray-500"
            data-cy="cf-save-status"
          >
            <i
              v-if="saving"
              class="fas fa-circle-notch fa-spin text-gray-400"
            ></i>
            <i
              v-else-if="!canSave"
              class="fas fa-circle-exclamation text-amber-500"
            ></i>
            <i v-else-if="form.id" class="fas fa-check text-green-500"></i>
            {{ saveStatus }}
          </span>
        </Transition>
        <button
          v-if="!reviewMode"
          type="button"
          class="flex items-center gap-1.5 font-medium text-blue-600 transition-colors hover:text-blue-800"
          data-cy="cf-review"
          @click="reviewMode = true"
        >
          <i class="fas fa-eye"></i> View for Review
        </button>
        <button
          v-else
          type="button"
          class="flex items-center gap-1.5 font-medium text-blue-600 transition-colors hover:text-blue-800"
          data-cy="cf-edit"
          @click="reviewMode = false"
        >
          <i class="fas fa-pen-to-square"></i> Back to editing
        </button>
      </div>
    </template>

    <div v-if="notFound" class="text-gray-600" data-cy="cf-not-found">
      This form could not be found.
    </div>

    <div v-else>
      <template v-if="reviewMode">
        <CustomFormReview :form="form" />
        <div class="flex justify-end pt-6 pb-8">
          <SeraButton text="Done" data-cy="cf-done" @click="done" />
        </div>
      </template>

      <template v-else>
        <div
          class="cf-header mb-4 space-y-4 rounded-lg border border-gray-200 bg-white p-4"
        >
          <InputField
            :model-value="form.name"
            title="Name"
            dense
            placeholder="Form name"
            :validation="nameTouched && !form.name ? 'error' : 'none'"
            :helper-text="nameTouched && !form.name ? 'Name is required' : ''"
            data-cy="cf-name"
            @focusout="nameTouched = true"
            @update:model-value="(v) => updateField('name', v)"
          />
          <TextArea
            :model-value="form.description"
            message="Description"
            placeholder="Optional"
            data-cy="cf-description"
            @update:model-value="(v) => updateField('description', v)"
          />
          <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
            <SelectInput
              :model-value="form.department_ids"
              label="Departments"
              placeholder="Any (ad-hoc only)"
              :options="departmentOptions"
              multiselect
              filter
              data-cy="cf-departments"
              @update:model-value="(v) => updateField('department_ids', v)"
            />
            <SelectInput
              :model-value="form.service_category_ids"
              label="Service Categories"
              placeholder="Any (ad-hoc only)"
              :options="serviceCategoryOptions"
              multiselect
              filter
              data-cy="cf-service-categories"
              @update:model-value="
                (v) => updateField('service_category_ids', v)
              "
            />
          </div>
          <Toggle
            :checked="form.required_for_close"
            label-after="Required to close job"
            data-cy="cf-required-close"
            @update:checked="(v) => updateField('required_for_close', v)"
          />
        </div>

        <div
          v-if="form.sections.length === 0"
          class="cf-enter mb-4 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 py-12 text-center"
          data-cy="cf-empty"
        >
          <i class="fas fa-list-check mb-3 text-3xl text-gray-300"></i>
          <p class="mb-4 text-sm text-gray-500">
            No sections yet — add your first section to start building.
          </p>
          <div class="flex justify-center">
            <SeraButton
              icon="fa fa-plus fa-fw"
              text="Add section"
              data-cy="add-section"
              @click="addSection"
            />
          </div>
        </div>

        <template v-else>
          <div class="mb-2 flex items-center justify-between">
            <h2
              class="text-xs font-semibold uppercase tracking-wide text-gray-500"
            >
              Sections
            </h2>
            <div class="flex items-center gap-3 text-sm">
              <button
                type="button"
                class="text-blue-600 transition-colors hover:text-blue-800"
                data-cy="cf-expand-all"
                @click="setAllExpanded(true)"
              >
                Expand all
              </button>
              <span class="text-gray-300">·</span>
              <button
                type="button"
                class="text-blue-600 transition-colors hover:text-blue-800"
                data-cy="cf-collapse-all"
                @click="setAllExpanded(false)"
              >
                Collapse all
              </button>
            </div>
          </div>

          <Draggable
            :model-value="form.sections"
            item-key="_key"
            handle=".cf-section-handle"
            :animation="150"
            ghost-class="cf-ghost"
            chosen-class="cf-chosen"
            class="space-y-3"
            data-cy="sections-draggable"
            @update:model-value="onSectionsReorder"
          >
            <template #item="{ element, index }">
              <CustomFormSectionEditor
                :model-value="element"
                :index="index"
                :total="form.sections.length"
                :expand-command="expandCommand"
                :active-question-key="activeQuestionKey"
                @update:model-value="(section) => updateSection(index, section)"
                @activate="setActiveQuestion"
                @toggle="onQuestionToggle"
                @remove="removeSection(index)"
                @duplicate="duplicateSection(index)"
                @move-up="moveSection(index, -1)"
                @move-down="moveSection(index, 1)"
              />
            </template>
          </Draggable>
        </template>

        <div class="flex items-center justify-between pt-8 pb-8">
          <SeraButton
            v-if="form.sections.length"
            icon="fa fa-plus fa-fw"
            text="Add section"
            data-cy="add-section"
            @click="addSection"
          />
          <span v-else></span>
          <SeraButton text="Done" data-cy="cf-done" @click="done" />
        </div>
      </template>
    </div>

    <CustomFormOutline
      v-if="!reviewMode && !notFound && form.sections.length > 1"
      :sections="form.sections"
      :active-question-key="activeQuestionKey"
      :open-question-keys="openKeyList"
      @jump-section="jumpToSection"
      @jump-question="jumpToQuestion"
    />
  </Page>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, reactive, ref } from "vue";
import { useRouter } from "vue-router";
import Draggable from "vuedraggable";
import { useStore } from "vuex";
import { updatePath } from "@/lib/url.js";

import SeraButton from "@/components/Button.vue";
import Page from "@/components/Page.vue";
import InputField from "@/components/Seranova/InputField/InputField.vue";
import SelectInput from "@/components/Seranova/SelectInput/SelectInput.vue";
import TextArea from "@/components/Seranova/TextArea/TextArea.vue";
import Toggle from "@/components/Seranova/Toggle/Toggle.vue";
import {
  customFormEditPath,
  customFormsIndexPath
} from "@/mixins/path_helpers";
import {
  type BuilderSection,
  cloneSection,
  newQuestion,
  newSection,
  typeHasOptions,
  typeHasUnit
} from "./customFormFields";
import CustomFormOutline from "./CustomFormOutline.vue";
import CustomFormReview from "./CustomFormReview.vue";
import CustomFormSectionEditor from "./CustomFormSectionEditor.vue";

const props = defineProps<{ templateId?: number | null }>();

const store = useStore();
const router = useRouter();

interface BuilderForm {
  id: number | null;
  published: boolean;
  name: string;
  description: string;
  required_for_close: boolean;
  department_ids: number[];
  service_category_ids: number[];
  sections: BuilderSection[];
}

const form = reactive<BuilderForm>({
  id: props.templateId ?? null,
  published: false,
  name: "",
  description: "",
  required_for_close: false,
  department_ids: [],
  service_category_ids: [],
  sections: []
});

const removedSectionIds = ref<number[]>([]);
const saving = ref(false);
const pending = ref(false);
// True from the first edit until the next successful save (covers the debounce
// window, an in-flight save, and edits that can't save yet). Drives the
// beforeunload guard so a hard refresh/close can't silently drop work.
const dirty = ref(false);
const serverError = ref<string | null>(null);
const notFound = ref(false);
const nameTouched = ref(false);
const reviewMode = ref(false);
let debounceTimer: ReturnType<typeof setTimeout> | null = null;

// --- Outline wayfinding -----------------------------------------------------
// activeQuestionKey = the row your cursor is in (drives the question rail and the
// outline's "current" markers). openQuestionKeys = every expanded question, so
// the outline can show which rows are open at a glance.
const activeQuestionKey = ref<string | null>(null);
const openQuestionKeys = reactive(new Set<string>());
const openKeyList = computed(() => [...openQuestionKeys]);

function setActiveQuestion(key: string) {
  activeQuestionKey.value = key;
}

function onQuestionToggle(key: string, isOpen: boolean) {
  if (isOpen) openQuestionKeys.add(key);
  else openQuestionKeys.delete(key);
}

// Jump the outline's target (a section or a question) to the top of the viewport
// and pulse it so the eye lands in the right place — the way around a 500-
// question form.
function jumpToSection(key: string) {
  scrollToAnchor(`[data-cf-section-key="${key}"]`);
}
function jumpToQuestion(key: string) {
  scrollToAnchor(`[data-cf-question-key="${key}"]`);
}

// Scroll the builder's OWN scroll container to the target, then pulse it. We
// deliberately avoid Element.scrollIntoView(): it walks every scrollable
// ancestor, including the app shell's overflow-hidden `w-dvw h-dvh` wrapper,
// which it will shove up by ~header-height — hiding the top bar and leaving a
// gap at the bottom that can't be scrolled back. Scrolling only the nearest
// scroll parent keeps the shell put.
function scrollToAnchor(selector: string) {
  const el = document.querySelector(selector) as HTMLElement | null;
  if (!el) return;
  const scroller = scrollParent(el);
  if (scroller) {
    const top =
      scroller.scrollTop +
      (el.getBoundingClientRect().top - scroller.getBoundingClientRect().top) -
      8;
    scroller.scrollTo({ top, behavior: "smooth" });
  } else {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }
  el.classList.add("cf-jump-pulse");
  window.setTimeout(() => el.classList.remove("cf-jump-pulse"), 1200);
}

function scrollParent(el: HTMLElement): HTMLElement | null {
  let node = el.parentElement;
  while (node) {
    const overflowY = getComputedStyle(node).overflowY;
    if (
      (overflowY === "auto" || overflowY === "scroll") &&
      node.scrollHeight > node.clientHeight
    ) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

const pageTitle = computed(() =>
  form.id ? "Edit Custom Form" : "New Custom Form"
);
// Autosave only when the whole tree is valid — the backend requires a template
// name, section names, and question titles, so saving an incomplete tree 422s.
// Drafts autosave in any state so no work is lost — only the form name is needed
// to create the record. A PUBLISHED form must stay complete (the backend rejects
// blanking a live form), so the full name/title gate still applies there.
const canSave = computed(() => {
  if (form.name.trim().length === 0) return false;
  if (!form.published) return true;
  return form.sections.every(
    (section) =>
      section.name.trim().length > 0 &&
      section.questions.every((q) => q.title.trim().length > 0)
  );
});
const saveStatus = computed(() => {
  if (saving.value) return "Saving…";
  if (form.name.trim().length === 0) return "Add a name to start saving";
  if (!canSave.value) return "Finish required fields to save";
  if (form.id) return "All changes saved";
  return "Not saved yet";
});

const departmentOptions = computed(() =>
  store.state.departments.map((d: { id: number; name: string }) => ({
    label: d.name,
    value: d.id
  }))
);
const serviceCategoryOptions = computed(() =>
  store.state.serviceCategories.map((sc: { id: number; name: string }) => ({
    label: sc.name,
    value: sc.id
  }))
);

onMounted(async () => {
  window.addEventListener("beforeunload", warnIfDirty);
  store.dispatch("fetchDepartments");
  store.dispatch("fetchServiceCategories");
  if (props.templateId != null) await load(props.templateId);
  syncBreadcrumb();
});

// Drive the app-header breadcrumb (Custom Forms › <name>) explicitly rather than
// leaving the nav's DOM-derived fallback to guess it, matching how JobPage and
// CustomerShow name their crumb.
function syncBreadcrumb() {
  const crumbs = [{ label: "Custom Forms", url: customFormsIndexPath() }];
  if (form.name) crumbs.push({ label: form.name });
  store.dispatch("setBreadcrumbs", crumbs);
}

onUnmounted(() => {
  window.removeEventListener("beforeunload", warnIfDirty);
  if (debounceTimer) clearTimeout(debounceTimer);
});

async function load(id: number) {
  try {
    const template = await store.dispatch("fetchCustomFormTemplate", id);
    hydrate(template);
  } catch (e) {
    notFound.value = true;
  }
}

function hydrate(json: any) {
  form.id = json.id;
  form.published = !!json.published_at;
  form.name = json.name ?? "";
  form.description = json.description ?? "";
  form.required_for_close = !!json.required_for_close;
  form.department_ids = json.department_ids ?? [];
  form.service_category_ids = json.service_category_ids ?? [];
  form.sections = (json.sections ?? []).map((section: any) => ({
    ...newSection(),
    id: section.id,
    name: section.name ?? "",
    description: section.description ?? "",
    questions: (section.questions ?? []).map((q: any) => ({
      ...newQuestion(),
      _key: `q-server-${q.id}`,
      id: q.id,
      title: q.title ?? "",
      description: q.description ?? "",
      question_type: q.question_type,
      unit: q.unit,
      required: q.required,
      photo_requirement: q.photo_requirement,
      photo_min_count: q.photo_min_count ?? 0,
      customer_visible: q.customer_visible,
      options: (q.options ?? []).map((o: any) => ({
        token: o.token,
        label: o.label
      }))
    }))
  }));
}

function updateField(key: keyof BuilderForm, value: unknown) {
  (form[key] as unknown) = value;
  scheduleSave();
}

function updateSection(index: number, section: BuilderSection) {
  form.sections[index] = section;
  scheduleSave();
}

function addSection() {
  form.sections.push(newSection());
  scheduleSave();
}

function removeSection(index: number) {
  const [removed] = form.sections.splice(index, 1);
  if (removed?.id) removedSectionIds.value.push(removed.id);
  scheduleSave();
}

function onSectionsReorder(sections: BuilderSection[]) {
  form.sections = sections;
  scheduleSave();
}

function duplicateSection(index: number) {
  const sections = [...form.sections];
  sections.splice(index + 1, 0, cloneSection(sections[index]));
  form.sections = sections;
  scheduleSave();
}

function moveSection(index: number, delta: number) {
  const target = index + delta;
  if (target < 0 || target >= form.sections.length) return;
  const sections = [...form.sections];
  [sections[index], sections[target]] = [sections[target], sections[index]];
  form.sections = sections;
  scheduleSave();
}

// Broadcast an expand/collapse-all to every section. The nonce forces the
// sections' watchers to fire even when the target value repeats.
const expandCommand = ref<{ expand: boolean; nonce: number }>({
  expand: true,
  nonce: 0
});
function setAllExpanded(expand: boolean) {
  expandCommand.value = { expand, nonce: expandCommand.value.nonce + 1 };
}

function scheduleSave() {
  dirty.value = true;
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(save, 800);
}

// Native "unsaved changes" prompt for a hard refresh/close/navigate-away — the
// one path autosave can't cover. SPA route changes don't fire beforeunload, and
// done() flushes first, so this only guards the raw-reload window.
function warnIfDirty(event: BeforeUnloadEvent) {
  if (!dirty.value) return;
  event.preventDefault();
  event.returnValue = "You have unsaved changes.";
}

async function save() {
  if (!canSave.value) return;
  if (saving.value) {
    pending.value = true;
    return;
  }
  saving.value = true;
  const sectionRefs = [...form.sections];
  const template = buildPayload();
  try {
    const response =
      form.id == null
        ? await create(template)
        : await store.dispatch("updateCustomFormTemplate", {
            id: form.id,
            template
          });
    reconcileIds(response.sections, sectionRefs);
    removedSectionIds.value = [];
    serverError.value = null;
    dirty.value = false;
  } catch (e) {
    serverError.value = extractError(e);
  } finally {
    saving.value = false;
    if (pending.value) {
      pending.value = false;
      save();
    }
  }
}

async function create(template: unknown) {
  const response = await store.dispatch("createCustomFormTemplate", template);
  form.id = response.id;
  // Reflect the new id in the address bar without a router navigation: swapping
  // routes would remount the builder and re-hydrate (collapsing the section the
  // admin is mid-edit on). This keeps the live instance while the deep link
  // still resolves on refresh (the edit route renders server-side).
  updatePath(customFormEditPath(response.id), true);
  syncBreadcrumb();
  return response;
}

// Done is a confirmation affordance (like the quote flow): edits are already
// autosaved, but clicking Done flushes any pending debounced save and returns
// to the library.
async function done() {
  if (debounceTimer) clearTimeout(debounceTimer);
  await save();
  router.push(customFormsIndexPath());
}

function buildPayload() {
  const sections_attributes = form.sections.map((section, sIndex) => {
    const questions_attributes: any[] = section.questions.map((q, qIndex) => ({
      ...(q.id ? { id: q.id } : {}),
      title: q.title,
      description: q.description,
      question_type: q.question_type,
      unit: typeHasUnit(q.question_type) ? q.unit : null,
      required: q.required,
      photo_requirement: q.photo_requirement,
      photo_min_count: q.photo_min_count,
      customer_visible: q.customer_visible,
      options: typeHasOptions(q.question_type) ? q.options : [],
      position: qIndex
    }));
    section.removedQuestionIds.forEach((id) =>
      questions_attributes.push({ id, _destroy: true })
    );
    return {
      ...(section.id ? { id: section.id } : {}),
      name: section.name,
      description: section.description,
      position: sIndex,
      questions_attributes
    };
  });
  removedSectionIds.value.forEach((id) =>
    sections_attributes.push({ id, _destroy: true })
  );

  return {
    name: form.name,
    description: form.description,
    required_for_close: form.required_for_close,
    department_ids: form.department_ids,
    service_category_ids: form.service_category_ids,
    sections_attributes
  };
}

// The server returns the surviving tree in position order — the same order we
// sent — so we zip response nodes to the snapshot refs and stamp server ids
// onto any node that didn't have one, without touching content the admin may
// have typed since the request went out.
function reconcileIds(responseSections: any[], sectionRefs: BuilderSection[]) {
  (responseSections ?? []).forEach((respSection, i) => {
    const ref = sectionRefs[i];
    if (!ref) return;
    if (ref.id == null) ref.id = respSection.id;
    ref.removedQuestionIds = [];
    (respSection.questions ?? []).forEach((respQuestion: any, j: number) => {
      const questionRef = ref.questions[j];
      if (questionRef && questionRef.id == null)
        questionRef.id = respQuestion.id;
    });
  });
}

function extractError(e: any): string {
  const data = e?.response?.data;
  if (data && typeof data === "object") {
    const messages = Object.entries(data).map(
      ([field, msgs]) =>
        `${field} ${([] as string[]).concat(msgs as any).join(", ")}`
    );
    if (messages.length) return messages.join("; ");
  }
  return "Could not save the form.";
}
</script>

<style scoped>
.cf-fade-enter-active,
.cf-fade-leave-active {
  transition: opacity 150ms ease;
}
.cf-fade-enter-from,
.cf-fade-leave-to {
  opacity: 0;
}

.cf-enter {
  animation: cf-enter 200ms ease;
}
@keyframes cf-enter {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Drag feedback for the sections list. */
.cf-ghost {
  opacity: 0.4;
}
.cf-chosen {
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
}

@media (prefers-reduced-motion: reduce) {
  .cf-fade-enter-active,
  .cf-fade-leave-active,
  .cf-enter {
    transition: none;
    animation: none;
  }
}
</style>
